import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, IconButton, Tooltip, Menu, MenuItem, ListSubheader } from '@mui/material';
import { Tune as ChooseIcon } from '@mui/icons-material';
import SectionErrorBoundary from '../components/SectionErrorBoundary.jsx';
import PaneEmpty from './PaneEmpty.jsx';

const DashboardPage = React.lazy(() => import('../pages/DashboardPage.jsx'));
const PatientManagementPage = React.lazy(() => import('../pages/PatientManagementPage.jsx'));
const SurgicalReadinessPage = React.lazy(() => import('../pages/SurgicalReadinessPage.jsx'));
const OrderManagementPage = React.lazy(() => import('../pages/OrderManagementPage.jsx'));
const ProtocolManagementPage = React.lazy(() => import('../pages/ProtocolManagementPage.jsx'));
const SurgeonsPage = React.lazy(() => import('../pages/SurgeonsPage.jsx'));
const SettingsPage = React.lazy(() => import('../pages/SettingsPage.jsx'));
const AdminPage = React.lazy(() => import('../pages/AdminPage.jsx'));
const ClinicalAnalyticsDashboard = React.lazy(() => import('../pages/ClinicalCommandCenter.jsx'));
const CommunicationsHub = React.lazy(() => import('../pages/CommunicationsHub.jsx'));
const IntegrationsPage = React.lazy(() => import('../pages/IntegrationsPage.jsx'));
const AuditPage = React.lazy(() => import('../pages/AuditPage.jsx'));
const AdminAnalyticsDashboard = React.lazy(() => import('../pages/AdminAnalyticsDashboard.jsx'));
const PatientDetailPage = React.lazy(() => import('../pages/PatientDetailPage.jsx'));
const UserProfilePage = React.lazy(() => import('../pages/UserProfilePage.jsx'));
// ASC pages
const ASCDashboardPage = React.lazy(() => import('../pages/asc/ASCDashboardPage.jsx'));
const ASCPatientManagementPage = React.lazy(() => import('../pages/asc/PatientManagementPage.jsx'));
const ASCProtocolManagementPage = React.lazy(
  () => import('../pages/asc/ProtocolManagementPage.jsx')
);
const ASCSurgeonManagementPage = React.lazy(() => import('../pages/asc/SurgeonManagementPage.jsx'));
const ASCSettingsPage = React.lazy(() => import('../pages/asc/SettingsPage.jsx'));
const ASCSurgerySchedulePage = React.lazy(() => import('../pages/asc/SurgerySchedulePage.jsx'));
const FacilityManagementPage = React.lazy(() => import('../pages/asc/FacilityManagementPage.jsx'));

// Derive a secondary location rendered by the primary Router
function toLocation(path) {
  try {
    const u = new URL(path, 'http://local');
    return { pathname: u.pathname, search: u.search, hash: u.hash };
  } catch {
    return { pathname: path || '/' };
  }
}

function useSecondaryLocation(initialPath, targetPath, onPathChange) {
  const primary = useLocation();
  const [loc, setLoc] = React.useState(() => toLocation(initialPath));
  React.useEffect(() => {
    if (targetPath) setLoc(toLocation(targetPath));
  }, [targetPath]);
  React.useEffect(() => {
    const current = (loc && loc.pathname) || '/';
    onPathChange?.(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc]);
  React.useEffect(() => {
    if (!loc || !loc.pathname) setLoc(toLocation('/dashboard'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary.pathname]);
  return loc;
}

const MENU_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Worklist', path: '/patient-management' },
  { label: 'Readiness', path: '/readiness' },
  { label: 'Orders', path: '/orders' },
  { label: 'Protocols', path: '/protocols' },
  { label: 'Surgeons', path: '/surgeons' },
  { label: 'Clinical Analytics', path: '/clinical/analytics' },
  { label: 'Business Analytics', path: '/admin/analytics' },
  { label: 'Settings', path: '/settings' },
  { label: 'Profile', path: '/profile' },
  { label: 'Admin', path: '/admin' },
  { label: 'Audit', path: '/audit' },
  { label: 'Integrations', path: '/integrations' },
  { label: 'Communications', path: '/communications' },
  // ASC section
  { label: 'ASC Dashboard', path: '/asc/dashboard' },
  { label: 'ASC Worklist', path: '/asc/patient-management' },
  { label: 'ASC Readiness', path: '/asc/readiness' },
  { label: 'ASC Orders', path: '/asc/orders' },
  { label: 'ASC Protocols', path: '/asc/protocol-management' },
  { label: 'ASC Surgeons', path: '/asc/surgeon-management' },
  { label: 'ASC Settings', path: '/asc/settings' },
  { label: 'ASC Profile', path: '/asc/profile' },
  { label: 'ASC Admin', path: '/asc/admin' },
  { label: 'ASC Audit', path: '/asc/audit' },
  { label: 'ASC Integrations', path: '/asc/integrations' },
  { label: 'ASC Schedule', path: '/asc/schedule' },
  { label: 'ASC Facilities', path: '/asc/facilities' },
];

export default function PaneRouter({ initialPath = '/dashboard', targetPath, onPathChange }) {
  const [menuEl, setMenuEl] = React.useState(null);
  const openMenu = e => setMenuEl(e.currentTarget);
  const closeMenu = () => setMenuEl(null);
  const secondaryLoc = useSecondaryLocation(initialPath, targetPath, onPathChange);

  return (
      <React.Suspense fallback={<Box sx={{ p: 2 }}>Loading…</Box>}>
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <Tooltip title="Choose content">
            <IconButton size="small" onClick={openMenu}>
              <ChooseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
            <ListSubheader disableSticky>Standard</ListSubheader>
            {MENU_ITEMS.filter(mi => !mi.path.startsWith('/asc')).map(mi => (
              <MenuItem
                key={mi.path}
                onClick={() => {
                  globalThis.dispatchEvent(
                    new CustomEvent('wb:open-in-pane', { detail: { path: mi.path } })
                  );
                  closeMenu();
                }}
              >
                {mi.label}
              </MenuItem>
            ))}
            <ListSubheader disableSticky>ASC</ListSubheader>
            {MENU_ITEMS.filter(mi => mi.path.startsWith('/asc')).map(mi => (
              <MenuItem
                key={mi.path}
                onClick={() => {
                  globalThis.dispatchEvent(
                    new CustomEvent('wb:open-in-pane', { detail: { path: mi.path } })
                  );
                  closeMenu();
                }}
              >
                {mi.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
          <SectionErrorBoundary sectionName="Secondary Pane">
            <Routes location={secondaryLoc}>
              <Route path="/__choose__" element={<PaneEmpty />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/patient-management" element={<PatientManagementPage />} />
              <Route path="/readiness" element={<SurgicalReadinessPage />} />
              <Route path="/orders" element={<OrderManagementPage />} />
              <Route path="/protocols" element={<ProtocolManagementPage />} />
              <Route path="/surgeons" element={<SurgeonsPage />} />
              <Route path="/clinical/analytics" element={<ClinicalAnalyticsDashboard />} />
              <Route path="/admin/analytics" element={<AdminAnalyticsDashboard />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/communications" element={<CommunicationsHub />} />
              {/* ASC routes */}
              <Route path="/asc/dashboard" element={<ASCDashboardPage />} />
              <Route path="/asc/patient-management" element={<ASCPatientManagementPage />} />
              <Route path="/asc/readiness" element={<SurgicalReadinessPage />} />
              <Route path="/asc/orders" element={<OrderManagementPage />} />
              <Route path="/asc/protocol-management" element={<ASCProtocolManagementPage />} />
              <Route path="/asc/surgeon-management" element={<ASCSurgeonManagementPage />} />
              <Route path="/asc/settings" element={<ASCSettingsPage />} />
              <Route path="/asc/profile" element={<UserProfilePage />} />
              <Route path="/asc/admin" element={<AdminPage />} />
              <Route path="/asc/audit" element={<AuditPage />} />
              <Route path="/asc/integrations" element={<IntegrationsPage />} />
              <Route path="/asc/schedule" element={<ASCSurgerySchedulePage />} />
              <Route path="/asc/facilities" element={<FacilityManagementPage />} />
              <Route path="/patient/:id" element={<PatientDetailPage />} />
              <Route path="*" element={<DashboardPage />} />
            </Routes>
          </SectionErrorBoundary>
        </Box>
      </React.Suspense>
  );
}
